import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    AlertCircle, X, Loader2, AlertTriangle, RefreshCw, 
    FileUp, CheckCircle2, Info, Timer, FileText,
    Settings, BarChart, FileDown, User, Download,
    Upload, Lock, ShieldCheck, Key
} from 'lucide-react';
import phoenicianLogo from '../../assets/PhoenicianLogo.png';
import pinConfig from './pin-config';
// Components imports with correct UI casing
import { Alert, AlertDescription, AlertTitle } from "@/components/UI/alert";
import { Button } from "@/components/UI/button";
import { useToast } from "@/components/UI/use-toast";
import { Progress } from "@/components/UI/progress";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/UI/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/UI/dialog";
import { Separator } from "@/components/UI/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/UI/tabs";
import { Input } from "@/components/UI/input";
import { FileUpload } from '@/components/LOAMS/FileUpload';
import { loamsApi } from '@/services/api';


// Lazy load heavy components to improve initial page load
const InvoiceManagement = lazy(() => import('@/components/LOAMS/InvoiceManagement'));
const DashboardReport = lazy(() => import('@/components/LOAMS/DashboardReport'));
const Export = lazy(() => import('@/components/LOAMS/Export'));
const SOA = lazy(() => import('@/components/LOAMS/Soa'));
const Edit = lazy(() => import('@/components/LOAMS/Edit'));
const File = lazy(() => import('@/components/LOAMS/File'));
const JCR = lazy(() => import('@/components/LOAMS/JCR'));
const MasterSheet = lazy(() => import('@/components/LOAMS/MasterSheet'));
const View = lazy(() => import('@/components/LOAMS/View'));
const Help = lazy(() => import('@/components/LOAMS/Help'));
const Ocr = lazy(() => import('@/components/LOAMS/Ocr'));

// Extract PIN Verification to separate component to reduce main component complexity
const PinVerification = ({ isOpen, onClose, onSuccess, pinType = 'verify' }) => {
    const { toast } = useToast();
    const [pin, setPin] = useState('');
    const [verifyPin, setVerifyPin] = useState('');
    const [currentPin, setCurrentPin] = useState('');
    const [defaultPin, setDefaultPin] = useState(''); // Added for default PIN verification
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [remainingAttempts, setRemainingAttempts] = useState(3);
    const [lockoutTime, setLockoutTime] = useState(null);
    const [isLocked, setIsLocked] = useState(false);
    const [setupStep, setSetupStep] = useState(1); // Added to track setup steps
    
    // Check if PIN exists or needs to be set up
    const [hasPinSetup, setHasPinSetup] = useState(false);
    
    const PIN_KEY = pinConfig.PIN_KEY;
    const PIN_ATTEMPTS_KEY = pinConfig.PIN_ATTEMPTS_KEY;
    const PIN_LOCKOUT_KEY = pinConfig.PIN_LOCKOUT_KEY;
    const DEFAULT_PIN = pinConfig.DEFAULT_PIN;

    useEffect(() => {
        // Check if PIN has been set up
        const storedPin = localStorage.getItem(PIN_KEY);
        setHasPinSetup(!!storedPin);
        
        // Check for lockout status
        const lockoutUntil = localStorage.getItem(PIN_LOCKOUT_KEY);
        if (lockoutUntil) {
            const lockoutTime = parseInt(lockoutUntil, 10);
            if (lockoutTime > Date.now()) {
                setIsLocked(true);
                setLockoutTime(lockoutTime);
                
                // Set up timer to check lockout status
                const interval = setInterval(() => {
                    if (Date.now() > lockoutTime) {
                        setIsLocked(false);
                        localStorage.removeItem(PIN_LOCKOUT_KEY);
                        clearInterval(interval);
                    }
                }, 1000);
                
                return () => clearInterval(interval);
            } else {
                localStorage.removeItem(PIN_LOCKOUT_KEY);
            }
        }
        
        // Reset attempts if we're starting fresh
        if (isOpen && !isLocked) {
            const attempts = localStorage.getItem(PIN_ATTEMPTS_KEY) || 3;
            setRemainingAttempts(parseInt(attempts, 10));
        }
        
        // Reset setup step when dialog opens
        if (pinType === 'setup') {
            setSetupStep(1);
        }
    }, [isOpen]);

    const handlePinChange = (e) => {
        const value = e.target.value;
        // Only allow numbers and limit to 4 digits
        if (/^\d*$/.test(value) && value.length <= 4) {
            setPin(value);
            setError(null);
        }
    };

    const handleVerifyPinChange = (e) => {
        const value = e.target.value;
        // Only allow numbers and limit to 4 digits
        if (/^\d*$/.test(value) && value.length <= 4) {
            setVerifyPin(value);
            setError(null);
        }
    };
    
    const handleCurrentPinChange = (e) => {
        const value = e.target.value;
        // Only allow numbers and limit to 4 digits
        if (/^\d*$/.test(value) && value.length <= 4) {
            setCurrentPin(value);
            setError(null);
        }
    };
    
    // New handler for default PIN input
    const handleDefaultPinChange = (e) => {
        const value = e.target.value;
        // Only allow numbers and limit to 4 digits
        if (/^\d*$/.test(value) && value.length <= 4) {
            setDefaultPin(value);
            setError(null);
        }
    };

    const handleVerifyPin = async () => {
        if (pin.length !== 4) {
            setError('PIN must be 4 digits');
            return;
        }

        setLoading(true);
        try {
            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 500));
            
            const storedPin = localStorage.getItem(PIN_KEY) || DEFAULT_PIN;
            
            if (pin === storedPin) {
                // Reset attempts on success
                localStorage.setItem(PIN_ATTEMPTS_KEY, '3');
                setRemainingAttempts(3);
                
                toast({
                    title: "Access Granted",
                    description: "PIN verification successful",
                    className: "bg-green-50 border-l-4 border-green-500",
                });
                
                onSuccess();
            } else {
                // Decrement attempts
                const newAttempts = remainingAttempts - 1;
                setRemainingAttempts(newAttempts);
                localStorage.setItem(PIN_ATTEMPTS_KEY, newAttempts.toString());
                
                if (newAttempts <= 0) {
                    // Lock out for 10 minutes
                    const lockoutUntil = Date.now() + (10 * 60 * 1000);
                    localStorage.setItem(PIN_LOCKOUT_KEY, lockoutUntil.toString());
                    setLockoutTime(lockoutUntil);
                    setIsLocked(true);
                    
                    toast({
                        variant: "destructive",
                        title: "Account Locked",
                        description: "Too many failed attempts. Try again in 10 minutes.",
                        duration: 5000,
                    });
                } else {
                    setError(`Incorrect PIN. ${newAttempts} attempts remaining.`);
                }
            }
        } catch (error) {
            console.error('PIN verification error:', error);
            setError('Verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    
    // Verify default PIN before allowing setup
    const handleVerifyDefaultPin = async () => {
        if (defaultPin.length !== 4) {
            setError('PIN must be 4 digits');
            return;
        }
        
        setLoading(true);
        try {
            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 500));
            
            if (defaultPin === DEFAULT_PIN) {
                // Default PIN is correct, proceed to next step
                setError(null);
                setSetupStep(2);
                toast({
                    title: "Default PIN Verified",
                    description: "Now set your personal PIN",
                    className: "bg-blue-50 border-l-4 border-blue-500",
                });
            } else {
                // Decrement attempts
                const newAttempts = remainingAttempts - 1;
                setRemainingAttempts(newAttempts);
                localStorage.setItem(PIN_ATTEMPTS_KEY, newAttempts.toString());
                
                if (newAttempts <= 0) {
                    // Lock out for 10 minutes
                    const lockoutUntil = Date.now() + (10 * 60 * 1000);
                    localStorage.setItem(PIN_LOCKOUT_KEY, lockoutUntil.toString());
                    setLockoutTime(lockoutUntil);
                    setIsLocked(true);
                    
                    toast({
                        variant: "destructive",
                        title: "Account Locked",
                        description: "Too many failed attempts. Try again in 10 minutes.",
                        duration: 5000,
                    });
                } else {
                    setError(`Incorrect default PIN. ${newAttempts} attempts remaining.`);
                }
            }
        } catch (error) {
            console.error('Default PIN verification error:', error);
            setError('Verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSetupPin = async () => {
        if (pin.length !== 4) {
            setError('PIN must be 4 digits');
            return;
        }
        
        if (pin !== verifyPin) {
            setError('PINs do not match');
            return;
        }
        
        setLoading(true);
        try {
            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 500));
            
            localStorage.setItem(PIN_KEY, pin);
            setHasPinSetup(true);
            
            toast({
                title: "PIN Set Successfully",
                description: "Your new PIN has been saved",
                className: "bg-green-50 border-l-4 border-green-500",
            });
            
            onSuccess();
            onClose();
        } catch (error) {
            console.error('PIN setup error:', error);
            setError('Failed to set PIN. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    
    const handleChangePin = async () => {
        if (pin.length !== 4 || verifyPin.length !== 4) {
            setError('PIN must be 4 digits');
            return;
        }
        
        if (pin !== verifyPin) {
            setError('New PINs do not match');
            return;
        }
        
        const storedPin = localStorage.getItem(PIN_KEY) || DEFAULT_PIN;
        if (currentPin !== storedPin) {
            setError('Current PIN is incorrect');
            return;
        }
        
        setLoading(true);
        try {
            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 500));
            
            localStorage.setItem(PIN_KEY, pin);
            
            toast({
                title: "PIN Changed Successfully",
                description: "Your PIN has been updated",
                className: "bg-green-50 border-l-4 border-green-500",
            });
            
            onClose();
        } catch (error) {
            console.error('PIN change error:', error);
            setError('Failed to change PIN. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (pinType === 'verify') {
                handleVerifyPin();
            } else if (pinType === 'setup') {
                if (setupStep === 1) {
                    handleVerifyDefaultPin();
                } else {
                    handleSetupPin();
                }
            } else if (pinType === 'change') {
                handleChangePin();
            }
        }
    };

    const formatLockoutTime = () => {
        if (!lockoutTime) return '';
        
        const remainingTime = Math.max(0, lockoutTime - Date.now());
        const minutes = Math.floor(remainingTime / 60000);
        const seconds = Math.floor((remainingTime % 60000) / 1000);
        
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };
    
    // Reset everything when closing
    const handleClose = () => {
        setPin('');
        setVerifyPin('');
        setCurrentPin('');
        setDefaultPin('');
        setError(null);
        setSetupStep(1);
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-[#6b563e] text-lg flex items-center">
                        <Lock className="mr-2 h-5 w-5 text-[#8b7355]" />
                        {pinType === 'verify' ? 'PIN Verification' : 
                         pinType === 'setup' ? 
                            (setupStep === 1 ? 'Default PIN Verification' : 'Set Up PIN') : 
                         'Change PIN'}
                    </DialogTitle>
                    <DialogDescription className="text-gray-500">
                        {pinType === 'verify' ? 
                          'Enter your PIN to access invoice management' : 
                         pinType === 'setup' ? 
                            (setupStep === 1 ? 
                                'Enter the default PIN provided by your administrator' : 
                                'Set up a 4-digit PIN for secure access') : 
                          'Change your current PIN'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Error Alert */}
                    {error && (
                        <Alert variant="destructive" className="bg-rose-50 border-rose-200">
                            <AlertCircle className="h-4 w-4 text-rose-600" />
                            <AlertDescription className="text-rose-700">{error}</AlertDescription>
                        </Alert>
                    )}
                    
                    {/* Lockout Warning */}
                    {isLocked && (
                        <Alert className="bg-amber-50 border-amber-200">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-700">
                                Account locked due to too many failed attempts. 
                                Try again in {formatLockoutTime()}.
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* PIN Input Fields */}
                    {pinType === 'verify' && (
                        <div className="space-y-2">
                            <div className="flex justify-center">
                                <div className="bg-gray-50 p-6 rounded-lg border-2 border-dashed border-gray-200">
                                    <Input
                                        type="password"
                                        placeholder="Enter 4-digit PIN"
                                        value={pin}
                                        onChange={handlePinChange}
                                        onKeyDown={handleKeyDown}
                                        className="text-center text-lg font-mono tracking-widest"
                                        maxLength={4}
                                        disabled={isLocked || loading}
                                        autoFocus
                                    />
                                </div>
                            </div>
                            
                            <p className="text-center text-sm text-gray-500 mt-2">
                                {hasPinSetup ? 
                                  'Enter your PIN to continue' : 
                                  'Enter the default PIN or set up a new one'}
                            </p>
                        </div>
                    )}
                    
                    {pinType === 'setup' && setupStep === 1 && (
                        <div className="space-y-4">
                            <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                                <div className="flex gap-3">
                                    <Key className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <h3 className="font-medium text-amber-800 text-sm">Default PIN Required</h3>
                                        <p className="text-sm text-amber-700 mt-1">
                                            You need to enter the default PIN provided by your administrator
                                            before you can set up your personal PIN.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Default PIN</label>
                                <Input
                                    type="password"
                                    placeholder="Enter default PIN"
                                    value={defaultPin}
                                    onChange={handleDefaultPinChange}
                                    onKeyDown={handleKeyDown}
                                    className="text-center text-lg font-mono tracking-widest"
                                    maxLength={4}
                                    disabled={loading}
                                    autoFocus
                                />
                            </div>
                        </div>
                    )}
                    
                    {pinType === 'setup' && setupStep === 2 && (
                        <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                                <div className="flex gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-blue-700">
                                        Default PIN verified successfully. Now set your personal PIN.
                                    </p>
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">New PIN</label>
                                <Input
                                    type="password"
                                    placeholder="Enter 4-digit PIN"
                                    value={pin}
                                    onChange={handlePinChange}
                                    className="text-center text-lg font-mono tracking-widest"
                                    maxLength={4}
                                    disabled={loading}
                                    autoFocus
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Confirm PIN</label>
                                <Input
                                    type="password"
                                    placeholder="Confirm 4-digit PIN"
                                    value={verifyPin}
                                    onChange={handleVerifyPinChange}
                                    onKeyDown={handleKeyDown}
                                    className="text-center text-lg font-mono tracking-widest"
                                    maxLength={4}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    )}
                    
                    {pinType === 'change' && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Current PIN</label>
                                <Input
                                    type="password"
                                    placeholder="Enter current PIN"
                                    value={currentPin}
                                    onChange={handleCurrentPinChange}
                                    className="text-center text-lg font-mono tracking-widest"
                                    maxLength={4}
                                    disabled={loading}
                                    autoFocus
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">New PIN</label>
                                <Input
                                    type="password"
                                    placeholder="Enter new PIN"
                                    value={pin}
                                    onChange={handlePinChange}
                                    className="text-center text-lg font-mono tracking-widest"
                                    maxLength={4}
                                    disabled={loading}
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Confirm New PIN</label>
                                <Input
                                    type="password"
                                    placeholder="Confirm new PIN"
                                    value={verifyPin}
                                    onChange={handleVerifyPinChange}
                                    onKeyDown={handleKeyDown}
                                    className="text-center text-lg font-mono tracking-widest"
                                    maxLength={4}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-3">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={loading}
                        className="text-[#8b7355] border-[#e6e4e1] hover:bg-gray-50"
                    >
                        Cancel
                    </Button>
                    
                    {pinType === 'verify' && (
                        <Button
                            onClick={handleVerifyPin}
                            disabled={pin.length !== 4 || isLocked || loading}
                            className="bg-[#8b7355] text-white hover:bg-[#6b563e]"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Verifying...
                                </>
                            ) : (
                                <>
                                    <ShieldCheck className="h-4 w-4 mr-2" />
                                    Verify
                                </>
                            )}
                        </Button>
                    )}
                    
                    {pinType === 'setup' && setupStep === 1 && (
                        <Button
                            onClick={handleVerifyDefaultPin}
                            disabled={defaultPin.length !== 4 || loading}
                            className="bg-[#8b7355] text-white hover:bg-[#6b563e]"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Verifying...
                                </>
                            ) : (
                                <>
                                    <Key className="h-4 w-4 mr-2" />
                                    Verify Default PIN
                                </>
                            )}
                        </Button>
                    )}
                    
                    {pinType === 'setup' && setupStep === 2 && (
                        <Button
                            onClick={handleSetupPin}
                            disabled={pin.length !== 4 || verifyPin.length !== 4 || loading}
                            className="bg-[#8b7355] text-white hover:bg-[#6b563e]"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Setting PIN...
                                </>
                            ) : (
                                <>
                                    <ShieldCheck className="h-4 w-4 mr-2" />
                                    Set PIN
                                </>
                            )}
                        </Button>
                    )}
                    
                    {pinType === 'change' && (
                        <Button
                            onClick={handleChangePin}
                            disabled={pin.length !== 4 || verifyPin.length !== 4 || currentPin.length !== 4 || loading}
                            className="bg-[#8b7355] text-white hover:bg-[#6b563e]"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Updating PIN...
                                </>
                            ) : (
                                'Change PIN'
                            )}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Utility functions
const getInitials = (name) => {
    if (!name) return 'U';
    return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
};

const getRandomColor = (name) => {
    const colors = [
        'bg-rose-500', 'bg-pink-500', 'bg-purple-500', 'bg-indigo-500',
        'bg-blue-500', 'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500',
        'bg-amber-500', 'bg-orange-500'
    ];
    
    const index = name ? 
        name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length
        : Math.floor(Math.random() * colors.length);
    
    return colors[index];
};

// Loading fallback component
const LoadingFallback = () => (
    <div className="flex justify-center items-center p-12">
        <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#8b7355]" />
            <p className="mt-4 text-[#8b7355]">Loading content...</p>
        </div>
    </div>
);

// Main Dashboard Component - Memoize for performance
const LOAMSDashboard = React.memo(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const reportRef = useRef(null);
    
    // State Management
    const [activeSection, setActiveSection] = useState('invoices');
    const [activeTab, setActiveTab] = useState('generate');
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileData, setFileData] = useState(null);
    const [previewCount, setPreviewCount] = useState(0);
    const [progress, setProgress] = useState(0);
    const [downloadReady, setDownloadReady] = useState(false);
    const [downloadData, setDownloadData] = useState(null);
    const [processingTime, setProcessingTime] = useState(null);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [serviceStatus, setServiceStatus] = useState({
        isChecking: true,
        isHealthy: false,
        lastChecked: null,
        details: null
    });
    
    // Tab switching loading state
    const [isTabLoading, setIsTabLoading] = useState(false);
    
    // PIN Verification related state
    const [showPinDialog, setShowPinDialog] = useState(false);
    const [showChangePinDialog, setShowChangePinDialog] = useState(false);
    const [showSetupPinDialog, setShowSetupPinDialog] = useState(false);
    const [isPinVerified, setIsPinVerified] = useState(false);
    const [pendingTabChange, setPendingTabChange] = useState(null);
    const [targetSection, setTargetSection] = useState(null);

    // Check if user has PIN setup
    const hasPinSetup = () => {
        return pinConfig.hasPinSetup();
    };

    // Check if user has permission to access PIN-protected areas
    const checkPinRequiredAccess = () => {
        return pinConfig.checkPinRequiredAccess();
    };

    const [avatarUrl, setAvatarUrl] = useState(() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user.avatarUrl || '';
    });
    const [showAvatarDialog, setShowAvatarDialog] = useState(false);
    const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleAvatarClick = () => {
        setShowAvatarDialog(true);
    };

    // Add a function to handle PIN menu click
    const handlePinMenuClick = () => {
        if (hasPinSetup()) {
            setShowChangePinDialog(true);
        } else {
            setShowSetupPinDialog(true);
        }
    };

    // Effect to check if user needs to set up PIN
    useEffect(() => {
        const needsPinSetup = checkPinRequiredAccess() && !hasPinSetup();
        if (needsPinSetup) {
            // Show toast notification for PIN setup
            toast({
                title: "PIN Setup Required",
                description: "Please set up a PIN for secure access to invoice management",
                action: (
                    <Button 
                        onClick={() => setShowSetupPinDialog(true)} 
                        variant="outline"
                        className="bg-amber-50 text-amber-600 hover:bg-amber-100"
                    >
                        Set Up Now
                    </Button>
                ),
                duration: 5000
            });
        }
    }, []);

    const getInitialSection = () => {
        const params = new URLSearchParams(location.search);
        return params.get('section') || 'invoices';
    };

    const getInitialTab = () => {
        const params = new URLSearchParams(location.search);
        return params.get('tab') || 'generate';
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
    
        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast({
                variant: "destructive",
                title: "File too large",
                description: "Please select an image under 2MB"
            });
            return;
        }
    
        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast({
                variant: "destructive",
                title: "Invalid file type",
                description: "Please select an image file"
            });
            return;
        }
    
        setSelectedAvatarFile(file);
    
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setAvatarUrl(reader.result);
        };
        reader.readAsDataURL(file);
    };
    
    const handleSaveAvatar = async () => {
        if (!selectedAvatarFile) return;
    
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedAvatarFile);
            formData.append('username', JSON.parse(localStorage.getItem('user'))?.username);
    
            const response = await loamsApi.uploadAvatar(formData);
    
            // Update local state and storage
            if (response.data.url) {
                setAvatarUrl(response.data.url);
                
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                user.avatarUrl = response.data.url;
                localStorage.setItem('user', JSON.stringify(user));
            }
    
            toast({
                title: "Success",
                description: "Profile picture updated successfully"
            });
    
            setShowAvatarDialog(false);
    
        } catch (error) {
            console.error('Avatar upload error:', error);
            toast({
                variant: "destructive",
                title: "Upload failed",
                description: error.message || "Failed to update profile picture"
            });
        } finally {
            setIsUploading(false);
        }
    };
    
    const handleRemoveAvatar = async () => {
        try {
            if (avatarUrl) {
                await loamsApi.removeAvatar();
            }
    
            setAvatarUrl('');
            setSelectedAvatarFile(null);
    
            // Update local storage
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            user.avatarUrl = null;
            localStorage.setItem('user', JSON.stringify(user));
    
            toast({
                title: "Success",
                description: "Profile picture removed successfully"
            });
    
            setShowAvatarDialog(false);
    
        } catch (error) {
            console.error('Avatar removal error:', error);
            toast({
                variant: "destructive",
                title: "Removal failed",
                description: "Failed to remove profile picture"
            });
        }
    };

    // Modified section change handler with loading state
    const handleSectionChange = (section) => {
        const sectionLower = section.toLowerCase();
        
        // Show loading state
        setIsTabLoading(true);
        
        // List of sections that require PIN verification
        const pinProtectedSections = ['edit', 'export'];
        
        // Check if we're navigating to a PIN-protected section or to Invoices with manage tab
        if (pinProtectedSections.includes(sectionLower) || 
            (sectionLower === 'invoices' && activeTab === 'manage')) {
            
            // If PIN is required for this user and not verified yet
            if (checkPinRequiredAccess() && !isPinVerified) {
                setPendingTabChange(sectionLower);
                setTargetSection(sectionLower);
                setShowPinDialog(true);
                setIsTabLoading(false); // Reset loading state
                return;
            }
        }
        
        // Use setTimeout to allow React to render loading state before heavy operations
        setTimeout(() => {
            setActiveSection(sectionLower);
            
            // Update URL with new section
            const params = new URLSearchParams(location.search);
            params.set('section', sectionLower);
            
            // If changing to invoices, ensure tab parameter is set
            if (sectionLower === 'invoices') {
                if (!params.has('tab')) {
                    params.set('tab', 'generate');
                }
                setActiveTab(params.get('tab'));
            } else {
                // Remove tab parameter for non-invoice sections
                params.delete('tab');
            }
            
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
            
            // Additional section-specific logic
            if (sectionLower === 'invoices') {
                setSelectedFile(null);
                setFileData(null);
                setError(null);
            } else if (sectionLower === 'report') {
                if (reportRef.current) {
                    reportRef.current.fetchReportData();
                }
            }
            
            setProgress(0);
            setDownloadReady(false);
            setDownloadData(null);
            
            // Remove loading state after a short delay to complete rendering
            setTimeout(() => {
                setIsTabLoading(false);
            }, 100);
        }, 10);
    };

    // Modified tab change handler with loading state
    const handleTabChange = (tab) => {
        // Show loading state
        setIsTabLoading(true);
        
        // Check if navigating to manage tab and PIN is required
        if (tab === 'manage' && checkPinRequiredAccess() && !isPinVerified) {
            setPendingTabChange(tab);
            setShowPinDialog(true);
            setIsTabLoading(false); // Reset loading state
            return;
        }
        
        // Use setTimeout to allow React to render loading state before heavy operations
        setTimeout(() => {
            setActiveTab(tab);
            const params = new URLSearchParams(location.search);
            params.set('tab', tab);
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
            
            // Remove loading state after a short delay to complete rendering
            setTimeout(() => {
                setIsTabLoading(false);
            }, 100);
        }, 10);
    };

    // Handle PIN verification success
    const handlePinSuccess = () => {
        setShowPinDialog(false);
        setIsPinVerified(true);
        
        // Process pending tab change if there is one
        if (pendingTabChange) {
            if (pendingTabChange === 'manage') {
                setActiveTab('manage');
                const params = new URLSearchParams(location.search);
                params.set('tab', 'manage');
                navigate(`${location.pathname}?${params.toString()}`, { replace: true });
            } else {
                setActiveSection(pendingTabChange);
                const params = new URLSearchParams(location.search);
                params.set('section', pendingTabChange);
                if (pendingTabChange === 'invoices' && !params.has('tab')) {
                    params.set('tab', 'manage');
                }
                navigate(`${location.pathname}?${params.toString()}`, { replace: true });
            }
            setPendingTabChange(null);
        }
        
        // Display success message based on the target section
        let successMessage = "Access granted";
        if (targetSection === 'invoices') {
            successMessage = "You can now manage invoices";
        } else if (targetSection === 'edit') {
            successMessage = "You can now access service agreements";
        } else if (targetSection === 'export') {
            successMessage = "You can now access data export";
        }
        
        toast({
            title: "Access Granted",
            description: successMessage,
            className: "bg-green-50 border-l-4 border-green-500",
            duration: 3000
        });
        
        setTargetSection(null);
    };

    const handleUploadComplete = (file, data) => {
        console.log('[LOAMS] Upload complete:', { file, data });
        setSelectedFile(file);
        setFileData(data);
        setPreviewCount(data.totalRows || 0);
        setError(null);
        setProgress(0);
        setDownloadReady(false);
        setDownloadData(null);
        setProcessingTime(estimateProcessingTime(data.totalRows));
        setShowSaveDialog(true);
    };

    const estimateProcessingTime = (rowCount) => {
        return Math.ceil(rowCount * 1 + 5);
    };

    const handleSaveDecision = async (saveToDb) => {
        setShowSaveDialog(false);
        if (!selectedFile) return;
    
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('save_to_db', saveToDb.toString());
    
            const response = await loamsApi.previewAndSave(formData, saveToDb);
            
            if (response.data.error) {
                throw new Error(response.data.details || response.data.error);
            }
            
            if (saveToDb) {
                toast({
                    title: "Data Saved Successfully",
                    description: "Would you like to generate invoices now?",
                    action: (
                        <Button 
                            onClick={handleGenerateInvoices} 
                            variant="secondary"
                            className="bg-amber-50 text-amber-600 hover:bg-amber-100"
                        >
                            Generate
                        </Button>
                    ),
                    duration: 5000
                });
            } else {
                toast({
                    title: "File Processed",
                    description: "Ready to generate invoices",
                    duration: 3000
                });
            }
        } catch (error) {
            console.error('[LOAMS] Save decision error:', error);
            setError(error.message);
            toast({
                variant: "destructive",
                title: "Operation Failed",
                description: error.message,
                duration: 5000
            });
        }
    };

    const handleGenerateInvoices = async () => {
        if (!selectedFile || !serviceStatus.isHealthy) return;

        setGenerating(true);
        setError(null);
        setProgress(0);
        setDownloadReady(false);
        setDownloadData(null);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            if (previewCount > 100) {
                toast({
                    title: "Large File Processing",
                    description: `Processing ${previewCount} rows may take several minutes.`,
                    variant: "warning",
                    duration: 8000
                });
            }

            const response = await loamsApi.generateInvoices(formData, (progressEvent) => {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                setProgress(percentCompleted);
            });

            setDownloadData(response.data);
            setDownloadReady(true);
            setProgress(100);

            toast({
                title: "Generation Successful",
                description: `Generated ${previewCount} invoices successfully`
            });

            // Trigger download
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoices_${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            console.error('[LOAMS] Generation error:', err);
            setError(err.message);
            setProgress(0);
            setDownloadReady(false);
            toast({
                variant: "destructive",
                title: "Generation Failed",
                description: err.message
            });
        } finally {
            setGenerating(false);
        }
    };

    const handleCancel = () => {
        setSelectedFile(null);
        setFileData(null);
        setPreviewCount(0);
        setError(null);
        setProgress(0);
        setDownloadReady(false);
        setDownloadData(null);
        setProcessingTime(null);
    };

    // Effects and Service Health Check
    useEffect(() => {
        checkServiceHealth();
        const interval = setInterval(checkServiceHealth, 60000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast({
                variant: "destructive",
                title: "Authentication Required",
                description: "Please login to continue"
            });
            navigate('/login');
        }
    }, [navigate, toast]);

    // Service Health Check
    const checkServiceHealth = async () => {
        try {
            const response = await loamsApi.getHealth();
            setServiceStatus({
                isChecking: false,
                isHealthy: response.data.status === 'healthy',
                lastChecked: new Date(),
                details: response.data
            });
        } catch (err) {
            setServiceStatus(prev => ({
                isChecking: false,
                isHealthy: false,
                lastChecked: new Date(),
                details: null
            }));
            toast({
                variant: "destructive",
                title: "Service Check Failed",
                description: "Unable to connect to the service. Please try again later."
            });
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const section = params.get('section');
        const tab = params.get('tab');
        
        if (section) {
            setActiveSection(section);
        }
        
        if (tab && section === 'invoices') {
            // If navigating directly to manage tab via URL, check PIN
            if (tab === 'manage' && checkPinRequiredAccess() && !isPinVerified) {
                setPendingTabChange('manage');
                setShowPinDialog(true);
            } else {
                setActiveTab(tab);
            }
        }
    }, [location.search]);
    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <header className="bg-white border-b border-[#e6e4e1] shadow-sm sticky top-0 z-50">
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center space-x-4">
                            <img 
                                src={phoenicianLogo} 
                                alt="Phoenician Logo" 
                                className="h-10 w-auto"
                            />
                            <Separator orientation="vertical" className="h-8" />
                            <h1 className="text-xl font-semibold text-[#8b7355]">
                                LOAMS
                            </h1>
                        </div>

                        <div className="flex items-center space-x-4">
                            {/* PIN Management Button - Only show for authorized roles */}
                            {checkPinRequiredAccess() && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handlePinMenuClick}
                                    className="text-[#8b7355] hover:text-[#6b563e]"
                                >
                                    <Key className="h-4 w-4 mr-2" />
                                    {hasPinSetup() ? 'Change PIN' : 'Set PIN'}
                                </Button>
                            )}
                            
                            {/* User Profile Section */}
                            <div className="flex items-center space-x-3">
                                <button 
                                    onClick={handleAvatarClick}
                                    className="relative w-10 h-10 rounded-full overflow-hidden hover:ring-2 hover:ring-offset-2 hover:ring-gray-300 focus:outline-none transition-all duration-200"
                                >
                                    {avatarUrl ? (
                                        <img 
                                            src={avatarUrl} 
                                            alt="User avatar"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className={`flex items-center justify-center w-full h-full ${getRandomColor(JSON.parse(localStorage.getItem('user'))?.displayName)} text-white font-medium`}>
                                            {getInitials(JSON.parse(localStorage.getItem('user'))?.displayName)}
                                        </div>
                                    )}
                                </button>
                                <div className="flex items-center">
                                    <span className="text-sm font-medium text-[#8b7355] mr-1">Hi,</span>
                                    <span className="text-sm font-medium text-[#6b563e]">
                                        {JSON.parse(localStorage.getItem('user'))?.displayName || 'User'}
                                    </span>
                                </div>
                            </div>

                            {/* Online/Offline Status Indicator */}
                            <div className="flex items-center">
                                <div className={`w-2.5 h-2.5 rounded-full ${
                                    serviceStatus.isChecking 
                                        ? 'bg-gray-400' 
                                        : serviceStatus.isHealthy 
                                            ? 'bg-emerald-500' 
                                            : 'bg-rose-500'
                                }`} />
                            </div>

                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={checkServiceHealth}
                                className="text-[#8b7355] hover:text-[#6b563e]"
                            >
                                <RefreshCw className={`h-4 w-4 ${serviceStatus.isChecking ? 'animate-spin' : ''}`} />
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Loading overlay that appears during tab transitions */}
            {isTabLoading && (
                <div className="fixed inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center">
                        <Loader2 className="h-10 w-10 text-[#8b7355] animate-spin" />
                        <p className="mt-4 text-[#6b563e] font-medium">Loading content...</p>
                    </div>
                </div>
            )}

            {/* Avatar Dialog */}
            <Dialog open={showAvatarDialog} onOpenChange={setShowAvatarDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Profile Picture</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        <div className="flex flex-col items-center gap-4">
                            <div className="relative w-24 h-24 rounded-full overflow-hidden">
                                {avatarUrl ? (
                                    <img 
                                        src={avatarUrl} 
                                        alt="Preview"
                                        className="w-full h-full object-cover"
                                        onError={() => setAvatarUrl('')}
                                    />
                                ) : (
                                    <div className={`flex items-center justify-center w-full h-full ${
                                        getRandomColor(JSON.parse(localStorage.getItem('user'))?.displayName)
                                    } text-white text-2xl font-medium`}>
                                        {getInitials(JSON.parse(localStorage.getItem('user'))?.displayName)}
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => document.getElementById('avatar-upload').click()}
                                    disabled={isUploading}
                                >
                                    <Upload className={`h-4 w-4 mr-2 ${isUploading ? 'animate-spin' : ''}`} />
                                    {isUploading ? 'Uploading...' : 'Upload'}
                                </Button>
                                {avatarUrl && (
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={handleRemoveAvatar}
                                        disabled={isUploading}
                                    >
                                        <X className="h-4 w-4 mr-2" />
                                        Remove
                                    </Button>
                                )}
                            </div>

                            <input
                                id="avatar-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setShowAvatarDialog(false)}
                            disabled={isUploading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveAvatar}
                            disabled={!selectedAvatarFile || isUploading}
                        >
                            {isUploading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Uploading...
                                </>
                            ) : 'Save'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
            {/* PIN Verification Dialogs */}
            <PinVerification 
                isOpen={showPinDialog} 
                onClose={() => {
                    setShowPinDialog(false);
                    setPendingTabChange(null);
                    setTargetSection(null);
                }}
                onSuccess={handlePinSuccess}
                pinType="verify"
            />
            
            <PinVerification 
                isOpen={showSetupPinDialog} 
                onClose={() => setShowSetupPinDialog(false)}
                onSuccess={() => {
                    toast({
                        title: "PIN Setup Complete",
                        description: "You can now access secure features",
                        className: "bg-green-50 border-l-4 border-green-500",
                    });
                }}
                pinType="setup"
            />
            
            <PinVerification 
                isOpen={showChangePinDialog} 
                onClose={() => setShowChangePinDialog(false)}
                onSuccess={() => {}}
                pinType="change"
            />
            
            <div className="min-h-screen bg-white">
                {/* Main tabs container */}
                <div className="w-full">
                    <Tabs value={activeSection} onValueChange={handleSectionChange} className="w-full">
                        {/* Sticky Navigation */}
                        <div className="sticky top-16 z-50 bg-white border-b border-[#e6e4e1] shadow-sm">
                            <TabsList className="h-14 w-full justify-start rounded-none bg-transparent p-0 border-b border-[#e6e4e1]">
                                <TabsTrigger 
                                    value="invoices" 
                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 
                                    focus-visible:ring-[#8b7355] rounded-none"
                                >
                                    Invoices
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="soa" 
                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 
                                    focus-visible:ring-[#8b7355] rounded-none"
                                >
                                    SOA
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="report" 
                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 
                                    focus-visible:ring-[#8b7355] rounded-none"
                                >
                                    Summary
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="edit" 
                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 
                                    focus-visible:ring-[#8b7355] rounded-none"
                                >
                                    Service Agreement
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="documents" 
                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 
                                    focus-visible:ring-[#8b7355] rounded-none"
                                >
                                    Documents
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="export" 
                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 
                                    focus-visible:ring-[#8b7355] rounded-none"
                                >
                                    Data
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="view" 
                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 
                                    focus-visible:ring-[#8b7355] rounded-none"
                                >
                                    Report
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="jcr" 
                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 
                                    focus-visible:ring-[#8b7355] rounded-none"
                                >
                                    JCR
                                </TabsTrigger>

                                <TabsTrigger 
                                    value="mastersheet" 
                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 
                                    focus-visible:ring-[#8b7355] rounded-none"
                                >
                                    MasterSheet
                                </TabsTrigger>

                                <TabsTrigger 
                                    value="help" 
                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 
                                    focus-visible:ring-[#8b7355] rounded-none"
                                >
                                    Help
                                </TabsTrigger>
                            </TabsList>
                        </div>
                    
                        {/* Content Sections - Use true conditional rendering for better performance */}
                        <div className="w-full">
                            {/* Invoices Section */}
                            <TabsContent value="invoices">
                                {activeSection === 'invoices' && (
                                    <div className="space-y-6 p-6">
                                        <Tabs value={activeTab} onValueChange={handleTabChange}>
                                            {/* Sub-tabs for Invoice section */}
                                            <TabsList className="h-14 w-full justify-start rounded-none bg-transparent p-0 border-b border-[#e6e4e1]">
                                                <TabsTrigger 
                                                    value="generate"
                                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                                    focus:outline-none rounded-none"
                                                >
                                                    Generate Invoices
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="manage"
                                                    className="px-6 py-3 text-sm font-medium border-b-2 border-transparent 
                                                    text-[#8b7355] hover:text-[#6b563e] transition-colors
                                                    data-[state=active]:border-[#8b7355] data-[state=active]:text-[#6b563e]
                                                    focus:outline-none rounded-none"
                                                >
                                                    Manage Invoices
                                                </TabsTrigger>
                                            </TabsList>

                                            {/* Generate Invoices Content - Use true conditional rendering */}
                                            <TabsContent value="generate">
                                                {activeTab === 'generate' && (
                                                    <div className="space-y-6">
                                                        {/* Error Alert */}
                                                        {error && (
                                                            <Alert variant="destructive" className="bg-rose-50 border-rose-200">
                                                                <AlertCircle className="h-4 w-4 text-rose-600" />
                                                                <AlertTitle className="text-rose-600 font-medium">Error</AlertTitle>
                                                                <AlertDescription className="text-rose-700">{error}</AlertDescription>
                                                            </Alert>
                                                        )}

                                                        {/* File Upload Section */}
                                                        {!selectedFile ? (
                                                            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                                                                <CardContent className="p-8">
                                                                    {/* Header */}
                                                                    <div className="flex items-center space-x-3 mb-6">
                                                                        <div className="p-2 bg-[#8b7355] rounded-lg">
                                                                            <FileText className="h-6 w-6 text-white" />
                                                                        </div>
                                                                        <div>
                                                                            <h2 className="text-xl font-semibold text-[#6b563e]">
                                                                                Invoice Generation
                                                                            </h2>
                                                                            <p className="text-[#8b7355] mt-1">
                                                                                Generate professional invoices from your Excel data
                                                                            </p>
                                                                        </div>
                                                                    </div>

                                                                    {/* Format Instructions */}
                                                                    <div className="bg-white border border-[#e6e4e1] rounded-lg p-6 mb-6">
                                                                        <h3 className="text-sm font-medium text-[#6b563e] mb-2">
                                                                            Required Format
                                                                        </h3>
                                                                        <p className="text-sm text-[#8b7355]">
                                                                            Please ensure your Excel file contains all required information 
                                                                            in the correct format before uploading.
                                                                        </p>
                                                                    </div>

                                                                    {/* Upload Component */}
                                                                    <FileUpload 
                                                                        onUploadComplete={handleUploadComplete}
                                                                        className="border-2 border-dashed border-[#e6e4e1] rounded-lg p-8
                                                                                hover:border-[#8b7355] transition-colors duration-200"
                                                                    />
                                                                </CardContent>
                                                            </Card>
                                                        ) : (
                                                            <Card className="bg-white shadow-sm">
                                                                {/* File details card content - same as before */}
                                                                {/* ... */}
                                                            </Card>
                                                        )}
                                                    </div>
                                                )}
                                            </TabsContent>

                                            {/* Manage Invoices Content - Lazy loaded */}
                                            <TabsContent value="manage">
                                                {activeTab === 'manage' && (
                                                    <Suspense fallback={<LoadingFallback />}>
                                                        <InvoiceManagement />
                                                    </Suspense>
                                                )}
                                            </TabsContent>
                                        </Tabs>
                                    </div>
                                )}
                            </TabsContent>

                            {/* Other Sections - All lazy loaded */}
                            <TabsContent value="report">
                                {activeSection === 'report' && (
                                    <div className="space-y-6 p-6">
                                        <Suspense fallback={<LoadingFallback />}>
                                            <DashboardReport ref={reportRef} />
                                        </Suspense>
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="export">
                                {activeSection === 'export' && (
                                    <Suspense fallback={<LoadingFallback />}>
                                        <Export />
                                    </Suspense>
                                )}
                            </TabsContent>

                            <TabsContent value="soa">
                                {activeSection === 'soa' && (
                                    <Suspense fallback={<LoadingFallback />}>
                                        <SOA />
                                    </Suspense>
                                )}
                            </TabsContent>

                            <TabsContent value="edit">
                                {activeSection === 'edit' && (
                                    <Suspense fallback={<LoadingFallback />}>
                                        <Edit />
                                    </Suspense>
                                )}
                            </TabsContent>

                            <TabsContent value="documents">
                                {activeSection === 'documents' && (
                                    <Suspense fallback={<LoadingFallback />}>
                                        <File />
                                    </Suspense>
                                )}
                            </TabsContent>

                            <TabsContent value="view">
                                {activeSection === 'view' && (
                                    <Suspense fallback={<LoadingFallback />}>
                                        <View />
                                    </Suspense>
                                )}
                            </TabsContent>

                            <TabsContent value="jcr">
                                {activeSection === 'jcr' && (
                                    <Suspense fallback={<LoadingFallback />}>
                                        <JCR />
                                    </Suspense>
                                )}
                            </TabsContent>

                            <TabsContent value="mastersheet">
                                {activeSection === 'mastersheet' && (
                                    <Suspense fallback={<LoadingFallback />}>
                                        <MasterSheet />
                                    </Suspense>
                                )}
                            </TabsContent>
                            

                            <TabsContent value="help">
                                {activeSection === 'help' && (
                                    <Suspense fallback={<LoadingFallback />}>
                                        <Help />
                                    </Suspense>
                                )}
                            </TabsContent>
                        </div>
                    </Tabs>
                </div>
            </div>
    
            {/* Save Dialog */}
<Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
    <DialogContent className="sm:max-w-md">
        <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#6b563e] flex items-center">
                <div className="p-2 bg-[#8b7355] rounded-lg mr-3">
                    <FileText className="h-5 w-5 text-white" />
                </div>
                Save to Database
            </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
            <div className="bg-white border border-[#e6e4e1] p-4 rounded-lg">
                <p className="text-sm text-[#6b563e]">
                    Would you like to save this data to the database? This will allow you to:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-[#8b7355]">
                    <li className="flex items-center">
                        <CheckCircle2 className="h-3 w-3 mr-2 text-[#8b7355]" />
                        Generate reports
                    </li>
                </ul>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
                <Button 
                    variant="outline" 
                    onClick={() => handleSaveDecision(false)}
                    className="border-[#e6e4e1] text-[#8b7355] hover:bg-gray-50"
                >
                    Generate Only
                </Button>
                <Button 
                    onClick={() => handleSaveDecision(true)}
                    className="bg-[#8b7355] text-white hover:bg-[#6b563e]"
                >
                    Save and Continue
                </Button>
            </div>
        </div>
    </DialogContent>
</Dialog>

{/* Processing Dialog */}
<Dialog open={generating}>
    <DialogContent className="sm:max-w-md">
        <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#6b563e] flex items-center">
                <div className="p-2 bg-[#8b7355] rounded-lg mr-3">
                    <Loader2 className="h-5 w-5 text-white animate-spin" />
                </div>
                Generating Invoices
            </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 pt-4">
            <div className="p-6 bg-white border border-[#e6e4e1] rounded-lg">
                <Progress value={progress} className="h-2 bg-[#e6e4e1]" />
                <div className="mt-4 text-center">
                    <p className="text-2xl font-semibold text-[#6b563e]">{progress}%</p>
                    <p className="text-sm text-[#8b7355] mt-1">
                        {progress === 100 ? 'Generation complete!' : 'Processing your invoices...'}
                    </p>
                </div>
            </div>
            <p className="text-sm text-[#a69781] text-center">
                Please don't close this window while processing
            </p>
        </div>
    </DialogContent>
</Dialog>

{/* Success Toast */}
{downloadReady && (
    <div className="fixed bottom-4 right-4 z-50">
        <Alert className="bg-white border border-emerald-200 shadow-lg">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <AlertTitle className="text-emerald-800 font-medium">Success</AlertTitle>
            <AlertDescription className="text-emerald-700">
                Your invoices have been generated successfully
            </AlertDescription>
        </Alert>
    </div>
)}
</div>
);
});

export default LOAMSDashboard;
